/**
 * OrderStepper Component
 * Visual step indicator for multi-step order flow
 * Mobile-first, accessible design with WCAG 2.1 AA compliance
 */

interface OrderStepperProps {
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
}

export function OrderStepper({ currentStep, totalSteps, stepLabels }: OrderStepperProps) {
  return (
    <nav aria-label="Order progress" className="mb-8">
      {/* Mobile: Simple progress text */}
      <div className="sm:hidden">
        <p className="text-sm font-medium text-gray-600">
          Step {currentStep} of {totalSteps}
        </p>
        <p className="text-lg font-semibold text-gray-900 mt-1">
          {stepLabels[currentStep - 1]}
        </p>
      </div>

      {/* Desktop: Visual stepper */}
      <ol className="hidden sm:flex items-center justify-center space-x-4">
        {stepLabels.map((label, index) => {
          const stepNumber = index + 1;
          const isComplete = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;

          return (
            <li key={stepNumber} className="flex items-center">
              {/* Step circle */}
              <div className="flex flex-col items-center">
                <div
                  className={`
                    flex items-center justify-center w-10 h-10 rounded-full border-2
                    transition-colors duration-200
                    ${
                      isComplete
                        ? 'bg-primary-500 border-primary-500 text-white'
                        : isCurrent
                          ? 'border-primary text-primary bg-white'
                          : 'border-gray-300 text-gray-400 bg-white'
                    }
                  `}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {isComplete ? (
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <span className="text-sm font-semibold">{stepNumber}</span>
                  )}
                </div>

                {/* Step label */}
                <span
                  className={`
                    mt-2 text-xs font-medium text-center
                    ${isCurrent ? 'text-primary' : 'text-gray-500'}
                  `}
                >
                  {label}
                </span>
              </div>

              {/* Connecting line (not after last step) */}
              {stepNumber < totalSteps && (
                <div
                  className={`
                    w-16 h-0.5 mx-2 mt-[-28px]
                    transition-colors duration-200
                    ${isComplete ? 'bg-primary-500' : 'bg-gray-300'}
                  `}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
